import React, { useEffect, useState, useContext, useRef } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { CartContext } from "../CartContext";
import { CiHeart, CiShoppingCart } from "react-icons/ci";
import { HiShoppingCart } from "react-icons/hi2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import video from "../assets/homeAssets/sprayVideo.mp4";
import { motion, useAnimation, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const MalePerfumes = () => {
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPrice, setFilterPrice] = useState([0, 1000]);
  const [filterSeasons, setFilterSeasons] = useState([]);
  const cart = useContext(CartContext);
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const handleWishlist = async (product) => {
    if (!currentUser) {
      toast.info("Please login to use wishlist", { autoClose: 1500 });
      return;
    }
    const inWishlist = cart.wishlist.find((item) => item.id === product.id);
    if (inWishlist) {
      cart.removeFromWishlist(product.id);
      toast.info("Removed from wishlist", { autoClose: 1500 });
    } else {
      const ok = await cart.addToWishlist(product);
      if (ok) {
        toast.success("Added to wishlist", { autoClose: 1500 });
      } else {
        toast.error("Could not add to wishlist", { autoClose: 2500 });
      }
    }
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const animation1 = useAnimation();

  useEffect(() => {
    if (isInView) {
      animation1.start("visible");
    }
    // eslint-disable-next-line
  }, [isInView]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsCollection = collection(db, "products");
        const productsSnapshot = await getDocs(productsCollection);
        const productsList = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          price: 12, // Default price for 30ml
        }));
        setProducts(
          productsList.filter(
            (product) =>
              product.category === "Male" || product.category === "Unisex"
          )
        );
      } catch (error) {
        console.error("Error fetching products: ", error);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = (productId) => {
    cart.addOneToCart(productId);
    toast.success("Product added to cart!", { autoClose: 1500 });
  };

  const handleRemoveFromCart = (productId) => {
    cart.removeOneFromCart(productId);
    toast.info("Product removed from cart!", { autoClose: 1500 });
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearchQuery =
      (product.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice =
      product.price >= filterPrice[0] && product.price <= filterPrice[1];
    const seasons = Array.isArray(product.seasons)
      ? product.seasons
      : [product.seasons];
    const matchesSeason =
      filterSeasons.length === 0 ||
      seasons.some((season) => filterSeasons.includes(season));

    return matchesSearchQuery && matchesPrice && matchesSeason;
  });

  const uniqueSeasons = [
    ...new Set(
      products.flatMap((product) =>
        Array.isArray(product.seasons) ? product.seasons : [product.seasons]
      )
    ),
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Parfumat për meshkuj | Premium Perfume KS";
  }, []);

  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 3.0;
    }
  }, []);

  return (
    <Container>
      <div className="banner">
        {/* <img src={bannerImg} alt="" /> */}
        <video
          ref={videoRef}
          src={video}
          loop
          autoPlay
          muted
          type="/mp4"
        ></video>
        <motion.h1
          className="title"
          ref={ref}
          variants={{
            hidden: { opacity: 0, y: 100 },
            visible: { opacity: 1, y: 0 },
          }}
          initial="hidden"
          animate={animation1}
          transition={{ duration: 0.4, delay: 0.2, type: "spring" }}
        >
          PARFUMAT PER <br /> <span>MESHKUJ</span>
        </motion.h1>
      </div>
      <ToastContainer />
      <SearchContainer>
        <input
          type="text"
          placeholder="Kërko brende ose produkt..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>
      <FilterContainer>
        <select
          value={filterSeasons}
          onChange={(e) => {
            const selectedOptions = Array.from(
              e.target.selectedOptions,
              (option) => option.value
            );
            if (selectedOptions.includes("")) {
              setFilterSeasons([]);
            } else {
              setFilterSeasons(selectedOptions);
            }
          }}
        >
          <option value="">Sezona</option>
          {uniqueSeasons.map((season) => (
            <option key={season} value={season}>
              {t(`season${season}`)}
            </option>
          ))}
        </select>
      </FilterContainer>
      <div className="items">
        {filteredProducts.map((product) => {
          const isInCart = cart.items.find((item) => item.id === product.id);

          return (
            <div className="item" key={product.id}>
              <Link to={`/products/${product.name}`} className="link-styles">
                <img src={product.image} alt={product.name} />
                <h3>{product.brand}</h3>
                <h2>{product.name}</h2>
              </Link>
              <h1>{product.price}€</h1>
              <span>
                {isInCart ? (
                  <HiShoppingCart
                    onClick={() => handleRemoveFromCart(product.id)}
                    className="icon"
                  />
                ) : (
                  <CiShoppingCart
                    onClick={() => handleAddToCart(product.id)}
                    className="icon"
                  />
                )}
                <CiHeart
                  className="icon"
                  onClick={() => handleWishlist(product)}
                  style={{
                    color: cart.wishlist.find((i) => i.id === product.id)
                      ? "var(--primaryColor)"
                      : "",
                  }}
                />
              </span>
            </div>
          );
        })}
      </div>
    </Container>
  );
};
const Container = styled.div`
  padding: 4em 0;
  .banner {
    /* height: 87vh; */
    display: flex;
    justify-content: end;
    position: relative;
    .title {
      text-align: center;
      font-family: "Comfortaa", sans-serif;
      font-weight: 200;
      font-size: 8vw;
      margin: 1em 5% 2em 0;
      z-index: 99;
      text-align: end;
      position: absolute;
      span {
        font-family: "The Seasons", sans-serif;
        color: var(--primaryColor);
        font-size: 9vw;
      }
    }
    video {
      filter: contrast(0.77) saturate(0.1);
      width: 100%;
      /* position: absolute; */
      top: 0;
      z-index: 0;
    }
  }
  .items {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 2em 0em;
    padding: 0 7%;
    .item {
      margin-bottom: 2em;
      width: 260px;
      padding: 7px 16px;
      height: 300px;
      text-align: start;
      border-right: 1px solid #555555;
      border-left: 1px solid #555555;
      cursor: pointer;
      transition: 0.4s;
      &:hover {
        background: #292929;
      }
      img {
        width: 100%;
        height: 50%;
        object-fit: contain;
      }
      h3 {
        font-weight: 500;
        margin-top: 10px;
        color: var(--primaryColor);
      }
      h2 {
        font-family: "Comfortaa", sans-serif;
        font-size: 19px;
      }
      h1 {
        font-size: 23px;
        font-weight: 200;
      }
      span {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 7px;
        margin-top: 1em;
        .icon {
          padding: 7px;
          width: 47%;
          font-size: 2.3em;
          border-radius: 10px;
          background: #303030;
          transition: 0.4s;
          cursor: pointer;
          &:hover {
            background: #222222;
          }
        }
      }
      button {
        background: white;
        font-family: "Comfortaa", sans-serif;
        padding: 9px 20px 7px 20px;
        border: none;
        border-radius: 30px;
        font-size: 15px;
        margin-top: 1em;
        cursor: pointer;
        transition: 0.4s;
        &:hover {
          background: var(--primaryColor);
          color: white;
        }
      }
    }
  }
  @media (max-width: 625px) {
    padding: 4em 1%;
    overflow: hidden;
    .banner {
      height: 40vh;
      video {
        left: 0;
        width: 140%;
        position: absolute;
      }
      .title {
        font-size: 10vw;
        span {
          font-size: 13vw;
        }
      }
    }
    .items {
      gap: 0;
      .item {
        width: 49%;
        text-align: start;
        h3 {
          font-size: 15px;
        }
        h2 {
          font-size: 16px;
        }
        h1 {
          font-size: 20px;
        }
      }
    }
  }
  @media (max-width: 450px) {
    padding: 4em 1%;
    .banner {
      height: 30vh;
      video {
        left: 0;
        width: 160%;
        position: absolute;
      }
      .title {
        font-size: 10vw;
      }
    }
  }
`;

const SearchContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 2em;
  margin-top: 3em;
  input {
    width: 50%;
    font-family: "Comfortaa";
    padding: 14px 20px;
    outline: none;
    background: none;
    color: white;
    box-shadow: 0 0 13px 2px #4d4d4d;
    border: none;
    border-radius: 30px;
    font-size: 16px;
  }
  @media (max-width: 500px) {
    input {
      width: 97%;
    }
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: auto;
  gap: 20px;
  margin-bottom: 4em;
  background: #303030;
  color: white;
  box-shadow: 0 0 13px 2px #303030;
  border-radius: 30px;
  padding: 10px;
  width: 400px;
  select,
  input {
    background: #303030;
    color: white;
    box-shadow: 0 0 13px 2px #303030;
    border-radius: 30px;
    padding: 10px;
    border: 1px solid #6d6d6d;
    border-radius: 5px;
    font-size: 16px;
  }
  @media (max-width: 500px) {
    width: 97%;
    border-radius: 14px;
    padding: 20px 10px;
  }
`;

export default MalePerfumes;
